/**
 * 🎯 Operations Center Service
 *
 * Aggregates real-time operational data from all modules:
 *  - Workflow instances & tasks (active, stalled, bottlenecks)
 *  - Issues/Problems (critical, open)
 *  - Actions (overdue)
 *  - Indicators (outside threshold)
 *  - Team workload
 *
 * Always filters by organization_id.
 */

import { supabase } from '../lib/supabaseClient';
import { getIndicatorHealth } from '../types/indicator';
import type { Indicator } from '../types/indicator';

// ─── Types ──────────────────────────────────────────────────

export interface OpsDashboardMetrics {
  active_workflows: number;
  overdue_tasks: number;
  critical_problems: number;
  actions_overdue: number;
  team_overload: number;
  sla_risk_percent: number;
  avg_lead_time_minutes: number;
  current_wip: number;
  waiting_tasks: number;
}

export interface OpsActiveWorkflow {
  id: string;
  title: string;
  template_name: string;
  status: string;
  priority: string;
  current_stage: string | null;
  current_stage_name: string | null;
  is_overdue: boolean;
  started_at: string | null;
  elapsed_minutes: number | null;
  sla_remaining_minutes: number | null;
  waiting_minutes: number | null;
  pause_reason?: string | null;
  task_status?: string | null;
  /** User IDs assigned to tasks in this instance */
  assigned_to_users: string[];
}

export interface OpsBottleneck {
  stage_name: string;
  stuck_count: number;
  avg_minutes: number;
}

export interface OpsCriticalProblem {
  id: string;
  title: string;
  severity: string;
  status: string;
  department: string | null;
  created_at: string;
  assigned_to: string | null;
}

export interface OpsOverdueAction {
  id: string;
  title: string;
  status: string;
  due_date: string;
  assigned_to: string | null;
  project_name: string | null;
  days_overdue: number;
}

export interface OpsTeamMember {
  user_id: string;
  user_name: string | null;
  tasks_active: number;
  tasks_overdue: number;
  load_level: 'normal' | 'high' | 'overload';
}

export type OpsAlertType = 'task_overdue' | 'workflow_stalled' | 'indicator_breach' | 'problem_recurrence';

export interface OpsAlert {
  id: string;
  type: OpsAlertType;
  title: string;
  detail: string;
  severity: 'warning' | 'critical';
  created_at: string;
  entity_id?: string;
  /** Context: execution/workflow name */
  context?: string;
  /** How long this alert has been waiting (minutes) */
  waiting_minutes?: number;
  /** Direct action route */
  action_route?: string;
}

export interface OpsNextBestTask {
  id: string;
  task_name: string;
  stage_name: string | null;
  instance_id: string;
  instance_title: string;
  sla_remaining_minutes: number | null;
  waiting_minutes: number;
  priority: string;
  is_overdue: boolean;
  reason: string;
  /** Numeric priority score (higher = more urgent). Recalculated every refresh. */
  score: number;
  /** Instance status ('active' | 'on_hold') */
  instance_status: string;
}

// ─── Service ────────────────────────────────────────────────

export class OperationsCenterService {
  /**
   * Fetch all dashboard metrics in parallel.
   */
  static async getDashboardMetrics(organizationId: string): Promise<OpsDashboardMetrics> {
    const [workflows, tasks, problems, actions, extended] = await Promise.all([
      OperationsCenterService.countActiveWorkflows(organizationId),
      OperationsCenterService.countOverdueTasks(organizationId),
      OperationsCenterService.countCriticalProblems(organizationId),
      OperationsCenterService.countOverdueActions(organizationId),
      OperationsCenterService.getExtendedMetrics(organizationId),
    ]);

    return {
      active_workflows: workflows,
      overdue_tasks: tasks,
      critical_problems: problems,
      actions_overdue: actions,
      team_overload: extended.team_overload,
      sla_risk_percent: extended.sla_risk_percent,
      avg_lead_time_minutes: extended.avg_lead_time_minutes,
      current_wip: extended.current_wip,
      waiting_tasks: extended.waiting_tasks,
    };
  }

  private static async getExtendedMetrics(orgId: string): Promise<{
    sla_risk_percent: number;
    avg_lead_time_minutes: number;
    current_wip: number;
    waiting_tasks: number;
    team_overload: number;
  }> {
    const [tasksData, instancesData] = await Promise.all([
      supabase
        .from('workflow_tasks_v9')
        .select('id, status, sla_deadline, is_overdue, assigned_to')
        .eq('organization_id', orgId)
        .in('status', ['pending', 'in_progress']),
      supabase
        .from('workflow_instances_v9')
        .select('id, total_duration_minutes')
        .eq('organization_id', orgId)
        .in('status', ['active', 'on_hold']),
    ]);

    const allTasks = tasksData.data ?? [];
    const activeTasks = allTasks.length;
    const overdue = allTasks.filter(t => t.is_overdue).length;
    const now = Date.now();
    const slaRisk = allTasks.filter(t => {
      if (t.is_overdue) return true;
      if (!t.sla_deadline) return false;
      const remaining = new Date(t.sla_deadline).getTime() - now;
      return remaining > 0 && remaining <= 60 * 60 * 1000;
    }).length;
    const sla_risk_percent = activeTasks > 0 ? Math.round(((overdue + slaRisk) / activeTasks) * 1000) / 10 : 0;
    const waiting_tasks = allTasks.filter(t => t.status === 'pending').length;

    const instances = instancesData.data ?? [];
    const current_wip = instances.length;
    const withDuration = instances.filter(i => typeof i.total_duration_minutes === 'number');
    const avg_lead_time_minutes = withDuration.length > 0
      ? withDuration.reduce((sum, i) => sum + (i.total_duration_minutes ?? 0), 0) / withDuration.length
      : 0;

    // Compute team overload from already-fetched tasks (avoids extra query)
    const userLoadMap = new Map<string, { active: number; overdue: number }>();
    for (const t of allTasks) {
      if (!t.assigned_to) continue;
      const entry = userLoadMap.get(t.assigned_to) ?? { active: 0, overdue: 0 };
      entry.active++;
      if (t.is_overdue) entry.overdue++;
      userLoadMap.set(t.assigned_to, entry);
    }
    let team_overload = 0;
    for (const v of userLoadMap.values()) {
      if (v.active >= 8 || v.overdue >= 3) team_overload++;
    }

    return { sla_risk_percent, avg_lead_time_minutes, current_wip, waiting_tasks, team_overload };
  }

  // ── Workflows ───────────────────────────────────────────

  private static async countActiveWorkflows(orgId: string): Promise<number> {
    const { data, error } = await supabase
      .from('workflow_instances_v9')
      .select('id')
      .eq('organization_id', orgId)
      .in('status', ['active', 'on_hold']);
    if (error) { console.warn('[OpsCenter] countActiveWorkflows failed:', error.message); return 0; }
    return data?.length ?? 0;
  }

  static async getActiveWorkflows(orgId: string): Promise<OpsActiveWorkflow[]> {
    const { data } = await supabase
      .from('workflow_instances_v9')
      .select('id, title, status, priority, current_stage_id, is_overdue, started_at, total_duration_minutes, template_id')
      .eq('organization_id', orgId)
      .in('status', ['active', 'on_hold'])
      .order('is_overdue', { ascending: false })
      .order('started_at', { ascending: true })
      .limit(50);

    if (!data) return [];

    const instanceIds = data.map(d => d.id);

    // Fetch template names + current tasks in parallel
    const templateIds = [...new Set(data.map(d => d.template_id).filter(Boolean))];
    const [templateResult, taskResult] = await Promise.all([
      templateIds.length > 0
        ? supabase.from('workflow_templates_v9').select('id, name').in('id', templateIds)
        : Promise.resolve({ data: [] as { id: string; name: string }[] }),
      (() => {
        let q = supabase
          .from('workflow_tasks_v9')
          .select('instance_id, stage_name, status, sla_deadline, created_at, assigned_to, pause_reason')
          .in('instance_id', instanceIds)
          .in('status', ['pending', 'in_progress', 'paused', 'PAUSED']);
        return q.order('created_at', { ascending: true });
      })(),
    ]);

    const templateMap: Record<string, string> = {};
    if (templateResult.data) {
      for (const t of templateResult.data) templateMap[t.id] = t.name;
    }

    // Build per-instance task info (first active/pending/paused task) + assigned users
    const now = Date.now();
    const instanceTaskMap = new Map<string, { stage_name: string | null; sla_remaining: number | null; waiting_minutes: number | null; pause_reason: string | null; task_status: string | null }>();
    const instanceUsersMap = new Map<string, Set<string>>();
    for (const task of taskResult.data ?? []) {
      // Collect all assigned users per instance
      if (task.assigned_to) {
        const set = instanceUsersMap.get(task.instance_id) ?? new Set();
        set.add(task.assigned_to as string);
        instanceUsersMap.set(task.instance_id, set);
      }
      if (instanceTaskMap.has(task.instance_id)) continue; // take first (earliest)
      const slaRemaining = task.sla_deadline
        ? Math.round((new Date(task.sla_deadline).getTime() - now) / 60000)
        : null;
      const waitingMin = task.status === 'pending' && task.created_at
        ? Math.round((now - new Date(task.created_at).getTime()) / 60000)
        : null;
      instanceTaskMap.set(task.instance_id, {
        stage_name: task.stage_name ?? null,
        sla_remaining: slaRemaining,
        waiting_minutes: waitingMin,
        pause_reason: task.pause_reason ?? null,
        task_status: task.status ?? null,
      });
    }

    return data.map(d => {
      const taskInfo = instanceTaskMap.get(d.id);
      return {
        id: d.id,
        title: d.title ?? 'Sem título',
        template_name: templateMap[d.template_id] ?? 'Desconhecido',
        status: d.status,
        priority: d.priority ?? 'media',
        current_stage: d.current_stage_id,
        current_stage_name: taskInfo?.stage_name ?? null,
        is_overdue: d.is_overdue ?? false,
        started_at: d.started_at,
        elapsed_minutes: d.total_duration_minutes,
        sla_remaining_minutes: taskInfo?.sla_remaining ?? null,
        waiting_minutes: taskInfo?.waiting_minutes ?? null,
        pause_reason: taskInfo?.pause_reason ?? null,
        task_status: taskInfo?.task_status ?? null,
        assigned_to_users: [...(instanceUsersMap.get(d.id) ?? [])],
      };
    });
  }

  // ── Bottlenecks ─────────────────────────────────────────

  static async getBottlenecks(orgId: string): Promise<OpsBottleneck[]> {
    // Tasks stuck in_progress or pending grouped by stage
    const { data } = await supabase
      .from('workflow_tasks_v9')
      .select('stage_name, started_at')
      .eq('organization_id', orgId)
      .in('status', ['pending', 'in_progress'])
      .not('stage_name', 'is', null);

    if (!data || data.length === 0) return [];

    const now = Date.now();
    const stageMap = new Map<string, { count: number; totalMinutes: number }>();
    for (const task of data) {
      const name = task.stage_name ?? 'Desconhecida';
      const entry = stageMap.get(name) ?? { count: 0, totalMinutes: 0 };
      entry.count++;
      if (task.started_at) {
        entry.totalMinutes += (now - new Date(task.started_at).getTime()) / 60000;
      }
      stageMap.set(name, entry);
    }

    return Array.from(stageMap.entries())
      .map(([stage_name, v]) => ({
        stage_name,
        stuck_count: v.count,
        avg_minutes: v.count > 0 ? Math.round(v.totalMinutes / v.count) : 0,
      }))
      .sort((a, b) => b.stuck_count - a.stuck_count)
      .slice(0, 10);
  }

  // ── Tasks (overdue) ─────────────────────────────────────

  private static async countOverdueTasks(orgId: string): Promise<number> {
    const { data, error } = await supabase
      .from('workflow_tasks_v9')
      .select('id')
      .eq('organization_id', orgId)
      .eq('is_overdue', true)
      .in('status', ['pending', 'in_progress']);
    if (error) { console.warn('[OpsCenter] countOverdueTasks failed:', error.message); return 0; }
    return data?.length ?? 0;
  }

  // ── Problems ────────────────────────────────────────────

  private static async countCriticalProblems(orgId: string): Promise<number> {
    const { data, error } = await supabase
      .from('issues')
      .select('id')
      .eq('organization_id', orgId)
      .eq('severity', 'critical')
      .neq('status', 'closed');
    if (error) { console.warn('[OpsCenter] countCriticalProblems failed:', error.message); return 0; }
    return data?.length ?? 0;
  }

  static async getCriticalProblems(orgId: string): Promise<OpsCriticalProblem[]> {
    const { data } = await supabase
      .from('issues')
      .select('id, title, severity, status, department, created_at, assigned_to')
      .eq('organization_id', orgId)
      .in('severity', ['critical', 'high'])
      .neq('status', 'closed')
      .order('severity', { ascending: true })
      .order('created_at', { ascending: false })
      .limit(20);

    return (data ?? []) as OpsCriticalProblem[];
  }

  // ── Actions (overdue) ───────────────────────────────────

  private static async countOverdueActions(orgId: string): Promise<number> {
    const { data } = await supabase
      .from('actions')
      .select('id, due_date')
      .eq('organization_id', orgId)
      .not('status', 'in', '(completed,cancelled)')
      .not('due_date', 'is', null)
      .lt('due_date', new Date().toISOString().slice(0, 10));
    return data?.length ?? 0;
  }

  static async getOverdueActions(orgId: string): Promise<OpsOverdueAction[]> {
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase
      .from('actions')
      .select('id, title, status, due_date, assigned_to, project_id')
      .eq('organization_id', orgId)
      .not('status', 'in', '(completed,cancelled)')
      .not('due_date', 'is', null)
      .lt('due_date', today)
      .order('due_date', { ascending: true })
      .limit(30);

    if (!data) return [];

    // Get project names
    const projectIds = [...new Set(data.map(d => d.project_id).filter(Boolean))];
    let projectMap: Record<string, string> = {};
    if (projectIds.length > 0) {
      const { data: projects } = await supabase
        .from('projects')
        .select('id, name')
        .in('id', projectIds);
      if (projects) {
        projectMap = Object.fromEntries(projects.map(p => [p.id, p.name]));
      }
    }

    const todayMs = new Date(today).getTime();
    return data.map(d => ({
      id: d.id,
      title: d.title,
      status: d.status,
      due_date: d.due_date,
      assigned_to: d.assigned_to,
      project_name: d.project_id ? (projectMap[d.project_id] ?? null) : null,
      days_overdue: Math.max(1, Math.round((todayMs - new Date(d.due_date).getTime()) / 86400000)),
    }));
  }

  // ── Team Workload ───────────────────────────────────────

  private static async countTeamOverload(orgId: string): Promise<number> {
    // Lightweight: count users with >=8 active or >=3 overdue from task data
    const { data } = await supabase
      .from('workflow_tasks_v9')
      .select('assigned_to, is_overdue')
      .eq('organization_id', orgId)
      .in('status', ['pending', 'in_progress'])
      .not('assigned_to', 'is', null);
    if (!data || data.length === 0) return 0;
    const userMap = new Map<string, { active: number; overdue: number }>();
    for (const t of data) {
      const uid = t.assigned_to as string;
      const entry = userMap.get(uid) ?? { active: 0, overdue: 0 };
      entry.active++;
      if (t.is_overdue) entry.overdue++;
      userMap.set(uid, entry);
    }
    let count = 0;
    for (const v of userMap.values()) {
      if (v.active >= 8 || v.overdue >= 3) count++;
    }
    return count;
  }

  static async getTeamWorkload(orgId: string): Promise<OpsTeamMember[]> {
    const { data } = await supabase
      .from('workflow_tasks_v9')
      .select('assigned_to, status, is_overdue')
      .eq('organization_id', orgId)
      .in('status', ['pending', 'in_progress'])
      .not('assigned_to', 'is', null);

    if (!data || data.length === 0) return [];

    // Group by user
    const userMap = new Map<string, { active: number; overdue: number }>();
    for (const task of data) {
      const uid = task.assigned_to as string;
      const entry = userMap.get(uid) ?? { active: 0, overdue: 0 };
      entry.active++;
      if (task.is_overdue) entry.overdue++;
      userMap.set(uid, entry);
    }

    // Get user names
    const userIds = [...userMap.keys()];
    let nameMap: Record<string, string> = {};
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, name')
        .in('user_id', userIds);
      if (profiles) {
        nameMap = Object.fromEntries(profiles.map(p => [p.user_id, p.name ?? p.user_id.slice(0, 8)]));
      }
    }

    return Array.from(userMap.entries())
      .map(([user_id, v]) => {
        let load_level: 'normal' | 'high' | 'overload' = 'normal';
        if (v.active >= 8 || v.overdue >= 3) load_level = 'overload';
        else if (v.active >= 5 || v.overdue >= 1) load_level = 'high';
        return {
          user_id,
          user_name: nameMap[user_id] ?? user_id.slice(0, 8),
          tasks_active: v.active,
          tasks_overdue: v.overdue,
          load_level,
        };
      })
      .sort((a, b) => b.tasks_active - a.tasks_active);
  }

  // ── Alerts ──────────────────────────────────────────────

  /**
   * Get the single most critical task that should be acted on next.
   * Priority: overdue SLA → highest priority → longest waiting.
   */
  /**
   * Priority scoring engine.
   * Scores: stopped +100, overdue +1000, SLA risk +500, priority rank ×100,
   *         waiting time ×0.1 (capped at 1440), rework +20 (future).
   * Recalculated every auto-refresh cycle.
   */
  static async getNextBestTask(orgId: string, userId?: string): Promise<OpsNextBestTask | null> {
    let query = supabase
      .from('workflow_tasks_v9')
      .select('id, task_name, stage_name, instance_id, sla_deadline, is_overdue, created_at, status, assigned_to')
      .eq('organization_id', orgId)
      .in('status', ['pending', 'in_progress']);
    if (userId) query = query.eq('assigned_to', userId);
    const { data } = await query
      .order('is_overdue', { ascending: false })
      .order('sla_deadline', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true })
      .limit(15);

    if (!data || data.length === 0) return null;

    // Get instance details (title + status for stopped detection)
    const instanceIds = [...new Set(data.map(d => d.instance_id))];
    const { data: instances } = await supabase
      .from('workflow_instances_v9')
      .select('id, title, status, priority')
      .in('id', instanceIds);
    const instanceMap: Record<string, { title: string; status: string; priority: string }> = {};
    for (const inst of instances ?? []) {
      instanceMap[inst.id] = { title: inst.title ?? 'Sem título', status: inst.status ?? 'active', priority: inst.priority ?? 'media' };
    }

    const now = Date.now();
    const PRIORITY_RANK: Record<string, number> = { critica: 4, alta: 3, media: 2, baixa: 1 };

    // Score each task
    const scored = data.map(t => {
      const slaRemaining = t.sla_deadline
        ? Math.round((new Date(t.sla_deadline).getTime() - now) / 60000)
        : null;
      const waitingMin = Math.round((now - new Date(t.created_at).getTime()) / 60000);
      const instInfo = instanceMap[t.instance_id];
      const pRank = PRIORITY_RANK[(instInfo?.priority ?? '').toLowerCase()] ?? 0;
      const isStopped = instInfo?.status === 'on_hold';

      let score = 0;
      // Scoring formula
      if (isStopped) score += 100;                         // stopped instance
      if (t.is_overdue) score += 1000;                     // SLA breached
      if (slaRemaining !== null && slaRemaining <= 60) score += 500; // SLA at risk
      score += pRank * 100;                                // priority rank
      score += Math.min(waitingMin, 1440);                 // waiting time (cap 24h)
      // Future: if (t.is_rework) score += 20;             // rework penalty

      let reason = 'Próxima na fila';
      if (isStopped) reason = 'Execução parada';
      else if (t.is_overdue) reason = 'SLA atrasado';
      else if (slaRemaining !== null && slaRemaining <= 60) reason = 'SLA em risco';
      else if (pRank >= 3) reason = `Prioridade ${instInfo?.priority ?? 'media'}`;
      else if (waitingMin > 120) reason = `Aguardando ${Math.round(waitingMin / 60)}h`;

      return { task: t, score, slaRemaining, waitingMin, reason, instanceStatus: instInfo?.status ?? 'active' };
    });

    scored.sort((a, b) => b.score - a.score);
    const best = scored[0];
    const instInfo = instanceMap[best.task.instance_id];

    return {
      id: best.task.id,
      task_name: best.task.task_name ?? 'Tarefa',
      stage_name: best.task.stage_name ?? null,
      instance_id: best.task.instance_id,
      instance_title: instInfo?.title ?? 'Sem título',
      sla_remaining_minutes: best.slaRemaining,
      waiting_minutes: best.waitingMin,
      priority: instanceMap[best.task.instance_id]?.priority ?? 'media',
      is_overdue: best.task.is_overdue ?? false,
      reason: best.reason,
      score: best.score,
      instance_status: best.instanceStatus,
    };
  }

  static async getAlerts(orgId: string): Promise<OpsAlert[]> {
    const alerts: OpsAlert[] = [];

    // Fetch all alert data in parallel
    const stalledThreshold = new Date();
    stalledThreshold.setDate(stalledThreshold.getDate() - 7);

    const [overdueResult, stalledResult, indicatorResult, issueResult] = await Promise.all([
      supabase
        .from('workflow_tasks_v9')
        .select('id, task_name, stage_name, sla_deadline, assigned_to')
        .eq('organization_id', orgId)
        .eq('is_overdue', true)
        .in('status', ['pending', 'in_progress'])
        .order('sla_deadline', { ascending: true }).limit(10),
      supabase
        .from('workflow_instances_v9')
        .select('id, title, started_at')
        .eq('organization_id', orgId)
        .eq('status', 'active')
        .lt('started_at', stalledThreshold.toISOString())
        .eq('is_overdue', true)
        .limit(10),
      supabase
        .from('indicators')
        .select('*')
        .eq('organization_id', orgId)
        .eq('status', 'active'),
      supabase
        .from('issues')
        .select('id, title, type, department, created_at')
        .eq('organization_id', orgId)
        .neq('status', 'closed')
        .not('department', 'is', null),
    ]);

    // 1. Overdue tasks
    if (overdueResult.data) {
      for (const t of overdueResult.data) {
        const waitMin = t.sla_deadline ? Math.round((Date.now() - new Date(t.sla_deadline).getTime()) / 60000) : 0;
        alerts.push({
          id: `task-${t.id}`,
          type: 'task_overdue',
          title: 'Tarefa atrasada',
          detail: `${t.task_name ?? 'Tarefa'} na etapa ${t.stage_name ?? '?'}`,
          severity: 'warning',
          created_at: t.sla_deadline ?? new Date().toISOString(),
          entity_id: t.id,
          context: t.stage_name ?? undefined,
          waiting_minutes: waitMin > 0 ? waitMin : undefined,
          action_route: '/minha-mesa',
        });
      }
    }

    // 2. Stalled workflows
    if (stalledResult.data) {
      for (const w of stalledResult.data) {
        const waitMin = w.started_at ? Math.round((Date.now() - new Date(w.started_at).getTime()) / 60000) : undefined;
        alerts.push({
          id: `wf-${w.id}`,
          type: 'workflow_stalled',
          title: 'Workflow parado',
          detail: w.title ?? 'Sem título',
          severity: 'critical',
          created_at: w.started_at ?? new Date().toISOString(),
          entity_id: w.id,
          context: w.title ?? undefined,
          waiting_minutes: waitMin,
          action_route: `/execucoes/instancias/${w.id}`,
        });
      }
    }

    // 3. Indicators outside threshold
    if (indicatorResult.data) {
      for (const ind of indicatorResult.data as Indicator[]) {
        const health = getIndicatorHealth(ind);
        if (health === 'critical') {
          alerts.push({
            id: `ind-${ind.id}`,
            type: 'indicator_breach',
            title: 'Indicador crítico',
            detail: `${ind.name}: ${ind.current_value} ${ind.unit} (meta: ${ind.target_value ?? '—'})`,
            severity: 'critical',
            created_at: ind.updated_at,
            entity_id: ind.id,
          });
        } else if (health === 'warning') {
          alerts.push({
            id: `ind-${ind.id}`,
            type: 'indicator_breach',
            title: 'Indicador em atenção',
            detail: `${ind.name}: ${ind.current_value} ${ind.unit}`,
            severity: 'warning',
            created_at: ind.updated_at,
            entity_id: ind.id,
          });
        }
      }
    }

    // 4. Problem recurrence
    if (issueResult.data && issueResult.data.length > 0) {
      const groups = new Map<string, typeof issueResult.data>();
      for (const iss of issueResult.data) {
        const key = `${iss.department}|${iss.type}`;
        const arr = groups.get(key) ?? [];
        arr.push(iss);
        groups.set(key, arr);
      }
      for (const [, group] of groups) {
        if (group.length >= 2) {
          alerts.push({
            id: `recur-${group[0].id}`,
            type: 'problem_recurrence',
            title: 'Recorrência detectada',
            detail: `${group.length}× "${group[0].type}" em ${group[0].department}`,
            severity: group.length >= 3 ? 'critical' : 'warning',
            created_at: group[0].created_at,
          });
        }
      }
    }

    return alerts
      .sort((a, b) => {
        if (a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
  }
}
